import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import {
  AuthProvider, useAuth, VENDAS, EDITAR_PEDIDO, OPERACIONAL, EMPRESAS, SO_MASTER, PROPOSTAS_LEITURA, DASHBOARD,
} from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "@/components/ui/sonner";

// Pages
import Login from "@/pages/Login";
import Checkout from "@/pages/Checkout";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import Orders from "@/pages/Orders";
import Propostas from "@/pages/Propostas";
import CreateOrder from "@/pages/CreateOrder";
import Companies from "@/pages/Companies";
import Users from "@/pages/Users";
import OrderManagement from "@/pages/OrderManagement";
import Financeiro from "@/pages/Financeiro";
import TinyErrors from "@/pages/TinyErrors";
import LinksBCMED from "@/pages/LinksBCMED";
import SimuladorCashBlack from "@/pages/SimuladorCashBlack";
import NotFound from "@/pages/NotFound";
import Layout from "@/components/Layout";
import ErrorBoundary, { bundleDesatualizado } from "@/components/ErrorBoundary";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

// Rota restrita por perfil. Os perfis vêm do AuthContext, os mesmos que o menu
// (Layout.js) usa: quem não tem acesso vai para `senao` (o Dashboard) em vez de
// abrir uma tela sem dados. Quem não tem Dashboard (Financiamentos) segue dali
// para as Propostas.
const RequireRole = ({ roles, senao = '/', children }) => {
  const { user } = useAuth();
  return roles.includes(user?.role) ? children : <Navigate to={senao} replace />;
};

// Bundle velho em cache foi causa real de suporte (checkout "Algo deu errado",
// split "só aparece cartão"): o usuário roda JS antigo contra a API nova. O
// ErrorBoundary já se auto-cura APÓS um crash; aqui a checagem roda no LOAD —
// versão publicada ≠ em execução -> recarrega 1x (guard de sessão anti-loop).
function VersionGuard() {
  useEffect(() => {
    (async () => {
      try {
        if (sessionStorage.getItem('app_auto_reload')) return;
        if (await bundleDesatualizado()) {
          sessionStorage.setItem('app_auto_reload', '1');
          window.location.reload();
        }
      } catch { /* melhor rodar desatualizado do que quebrar o load */ }
    })();
  }, []);
  return null;
}

function AppRoutes() {
  return (
    <>
    <VersionGuard />
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/checkout/:token" element={<Checkout />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<RequireRole roles={DASHBOARD} senao="/propostas"><Dashboard /></RequireRole>} />
        <Route path="produtos" element={<Products />} />
        <Route path="pedidos" element={<RequireRole roles={VENDAS}><Orders /></RequireRole>} />
        <Route path="pedidos/novo" element={<RequireRole roles={VENDAS}><CreateOrder /></RequireRole>} />
        <Route path="pedidos/editar/:id" element={<RequireRole roles={EDITAR_PEDIDO}><CreateOrder /></RequireRole>} />
        <Route path="propostas" element={<RequireRole roles={PROPOSTAS_LEITURA}><Propostas /></RequireRole>} />
        <Route path="propostas/nova" element={<RequireRole roles={VENDAS}><CreateOrder modo="proposta" /></RequireRole>} />
        <Route path="propostas/editar/:id" element={<RequireRole roles={VENDAS}><CreateOrder modo="proposta" /></RequireRole>} />
        <Route path="simulador-cash-black" element={<RequireRole roles={VENDAS}><SimuladorCashBlack /></RequireRole>} />
        <Route path="gestao-pedidos" element={<RequireRole roles={OPERACIONAL}><OrderManagement /></RequireRole>} />
        <Route path="financeiro" element={<RequireRole roles={OPERACIONAL}><Financeiro /></RequireRole>} />
        <Route path="pedidos-erro-tiny" element={<RequireRole roles={OPERACIONAL}><TinyErrors /></RequireRole>} />
        <Route path="empresas" element={<RequireRole roles={EMPRESAS}><Companies /></RequireRole>} />
        <Route path="links-bcmed" element={<LinksBCMED />} />
        <Route path="usuarios" element={<RequireRole roles={SO_MASTER}><Users /></RequireRole>} />
        {/* Nested "*" cobre qualquer rota não mapeada dentro do Layout — o
            parent "/" já engole todo path desconhecido antes de chegar aqui. */}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <ThemeProvider>
            <AuthProvider>
              <AppRoutes />
              <Toaster position="top-right" />
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
